import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Container,
  Stack,
  Icon,
  Zoom,
  Fab,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/Redeem";
import CustomerContext from "../../../context";
import { CardSkeleton } from "../../../../../../components/misc/common";
import { FCol } from "../../../../../../globalComponents/standard";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const PointsProductsList = () => {
  const { rewardsRequest, getRewards, balances } = useContext(CustomerContext);
  const { loading, data: rewards } = rewardsRequest;
  const [showButton, setShowButton] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Se a div do topo NÃO está visível, mostramos o botão
        setShowButton(!entry.isIntersecting);
      },
      { threshold: 0.1 }, // Dispara quando 10% da div sumir/aparecer
    );

    if (topRef.current) {
      observer.observe(topRef.current);
    }

    if (!rewards.length) getRewards();

    return () => {
      if (topRef.current) observer.unobserve(topRef.current);
    };
  }, []);

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, pb: 5 }}>
      {/* Âncora para o scroll voltar aqui */}
      <div ref={topRef} style={{ height: "1px", width: "100%" }} />
      {loading ? (
        <Grid container spacing={2}>
          {Array.from(new Array(8)).map((_, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={index}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : !rewards.length ? (
        <EmptyComponent onRefresh={getRewards} />
      ) : (
        <Grid container spacing={4} justifyContent="center">
          {rewards.map((reward) => (
            <Grid key={reward._id}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  width: 300,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  bgcolor: "#fff",
                  border: "1px solid #f0f0f0",
                  "&:hover": {
                    elevation: 6,
                    borderColor: "secondary.main",
                    transform: "translateY(-3px)",
                    transition: "all 0.3s ease-in-out",
                  },
                }}
              >
                {/* ÁREA DA IMAGEM */}
                <CardMedia
                  component="img"
                  sx={{
                    height: 250,
                    objectFit: "scale-down",

                    bgcolor: "#f5f5f5", // Um cinza bem claro para o fundo da foto
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  image={reward.imageUrl}
                  alt={reward.title}
                />

                {/* ÁREA DE CONTEÚDO (cinza escuro como na imagem) */}
                <CardContent
                  sx={{
                    flexGrow: 1,
                    bgcolor: "#424242", // Cinza escuro original
                    color: "#fff",
                    p: 3,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ opacity: 0.7, color: "#e0e0e0" }}
                    >
                      VALOR EM PONTOS
                    </Typography>
                    <Typography
                      variant="h3"
                      component="div"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: -1,
                        fontSize: { xs: "2.5rem", sm: "3rem" },
                      }}
                    >
                      {reward.requiredPoints.toLocaleString("pt-BR")}
                    </Typography>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ mt: -0.5, color: "#bdbdbd" }}
                    >
                      Pontos
                    </Typography>
                  </Stack>

                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        lineHeight: 1.3,
                      }}
                    >
                      {reward.title}
                    </Typography>
                  </Box>

                  {/* BOTÃO REAL (Com hover e ícone) */}
                  {reward.elegible ? (
                    <Button
                      onClick={reward.showRedeemInfo}
                      variant="contained"
                      color="secondary" // Usando a cor de destaque (amarela/laranja)
                      size="large"
                      fullWidth
                      startIcon={<ShoppingCartIcon />}
                      sx={{
                        borderRadius: 3,
                        fontWeight: 800,
                        py: 1.5,
                        color: "#fff",
                        // Substituindo o texto puro laranja por um botão real
                        bgcolor: "primary.main",
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      }}
                    >
                      SOLICITAR RESGATE
                    </Button>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ opacity: 0.7, color: "#e0e0e0" }}
                    >
                      Faltam {reward.remaingPoints.toLocaleString("pt-BR")}{" "}
                      pontos pra resgatar esse!
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Zoom in={showButton}>
        <Box
          onClick={scrollToTop}
          sx={{ position: "fixed", bottom: 32, right: 32, zIndex: 9999 }}
        >
          <Fab color="primary" size="large">
            <KeyboardArrowUpIcon />
          </Fab>
        </Box>
      </Zoom>
    </Container>
  );
};

const EmptyComponent = ({ onRefresh }) => {
  return (
    <FCol justifyContent="center" marginTop={"25dvh"} fullWidth>
      <Icon
        color="disabled"
        style={{ fontSize: 96, alignSelf: "center", textAlign: "center" }}
      >
        playlist_remove
      </Icon>
      <Typography align="center" variant="h5">
        Nenhum registro encontrado
      </Typography>
      <span style={{ alignSelf: "center", marginTop: 30 }}>
        <Button onClick={onRefresh} color="secondary" variant="text">
          Atualizar
        </Button>
      </span>
    </FCol>
  );
};

export default PointsProductsList;
